<!--
  Vista PLANTILLA del informe detallado: calco del prototipo (EPC Tracker · Informes X-ELIO).
  De momento es solo presentación con datos de demostración (como el prototipo) para validar
  el diseño; aún NO está cableada a los datos reales ni traducida (i18n pendiente).
  El chrome de la app (sidebar, barra de proyecto, pestañas) lo pone dashboard-report.vue.
-->
<template>
  <div class="rtpl">
    <!-- ① Periodo -->
    <div class="rtpl-card rtpl-step">
        <div class="rtpl-step-h">
          <span class="rtpl-n">1</span>
          <b-icon pack="fas" icon="calendar-alt" size="is-small" class="text-blue-darker" />
          <h3>{{ $t('period') }}</h3>
          <span class="rtpl-chip">{{ periodLabel }}</span>
          <b-button class="ml-auto" type="is-primary" size="is-small" outlined
            icon-pack="fas" icon-left="calendar-week" @click="adjustToIso">{{ $t('adjust_to_iso_week') }}</b-button>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-4 gap-3">
          <b-field :label="$t('week') + ' ISO'" class="mb-0">
            <b-numberinput v-model="week" :min="1" :max="53" controls-position="compact"
              size="is-small" expanded icon-pack="fas" @keypress.native="onlyNumber" />
          </b-field>
          <b-field :label="$t('year')" class="mb-0">
            <b-numberinput v-model="year" :min="2000" :max="2100" controls-position="compact"
              size="is-small" expanded icon-pack="fas" @keypress.native="onlyNumber" />
          </b-field>
          <b-field :label="$t('start_date')" class="mb-0">
            <b-datepicker v-model="dateFrom" size="is-small" icon-pack="fas" icon-right="calendar"
              :first-day-of-week="parseInt($t('first_day_of_week'))"
              :month-names="$t('months_names')" :day-names="$t('days_names')" />
          </b-field>
          <b-field :label="$t('end_date')" class="mb-0">
            <b-datepicker v-model="dateTo" size="is-small" icon-pack="fas" icon-right="calendar"
              :first-day-of-week="parseInt($t('first_day_of_week'))"
              :month-names="$t('months_names')" :day-names="$t('days_names')" />
          </b-field>
        </div>
        <div class="inline-flex items-center gap-1 text-xs font-semibold mt-2"
          :class="isoComplete ? 'text-green-dark' : 'text-orange-dark'">
          <b-icon :icon="isoComplete ? 'check-circle' : 'exclamation-circle'" pack="fas" size="is-small" />
          <span v-if="isoComplete">{{ $t('iso_week_complete') }}</span>
          <span v-else>{{ $t('custom_period_not_iso', { week, year }) }}</span>
        </div>

        <div class="rtpl-collapse">
          <div class="rtpl-collapse-h" role="button" tabindex="0"
            @click="cfgOpen = !cfgOpen" @keydown.enter="cfgOpen = !cfgOpen" @keydown.space.prevent="cfgOpen = !cfgOpen">
            <b-icon pack="fas" :icon="cfgOpen ? 'chevron-up' : 'chevron-down'" size="is-small" />
            <span class="rtpl-collapse-t">{{ $t('report_config') }}</span>
            <span class="rtpl-cfg-edit ml-auto">{{ cfgOpen ? $t('close') : $t('edit') }}</span>
          </div>

          <!-- Abierto: formulario -->
          <template v-if="cfgOpen">
            <div class="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3">
              <b-field :label="$t('title')" class="mb-0 md:col-span-2">
                <b-input v-model="cfgTitle" size="is-small" disabled />
              </b-field>
              <b-field :label="$t('language')" class="mb-0">
                <b-select v-model="cfgLang" size="is-small" expanded disabled>
                  <option>Español (ES)</option>
                  <option>English (EN)</option>
                </b-select>
              </b-field>
              <b-field :label="$t('author')" class="mb-0">
                <b-input v-model="cfgAuthor" size="is-small" disabled />
              </b-field>
            </div>
            <div class="rtpl-block-lab">{{ $t('content_to_include') }} <span class="rtpl-note">· {{ $t('defined_in_project_config') }}</span></div>
            <div class="flex flex-wrap gap-x-5 gap-y-2">
              <b-checkbox v-for="it in cfgInclude" :key="it.key" v-model="it.on" size="is-small" disabled>{{ $t(it.labelKey) }}</b-checkbox>
            </div>
          </template>

          <!-- Cerrado: resumen en gris -->
          <div v-else class="rtpl-cfg-summary">
            <div>{{ $t('title') }}: {{ cfgTitle }}</div>
            <div>{{ $t('language') }} {{ cfgLang }} · {{ $t('author') }}: {{ cfgAuthor }} · {{ $t('include') }}: {{ includeSummary }}</div>
          </div>
        </div>
      </div>

      <!-- ② Apartados -->
      <div class="rtpl-card rtpl-step">
        <div class="rtpl-step-h">
          <span class="rtpl-n">2</span>
          <b-icon pack="fas" icon="th-large" size="is-small" class="text-blue-darker" />
          <h3>{{ $t('report_sections') }}</h3>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div v-for="(s, i) in visibleSections" :key="'apt_'+i" class="rtpl-apt">
            <span class="rtpl-toggle" :class="{ on: s.print, 'is-locked': s.disabled }" @click="toggleSection(s)"></span>
            <span class="rtpl-apt-label">
              <span class="rtpl-apt-name">{{ s.name }}</span><span v-if="s.disabled" class="rtpl-apt-ob"> · {{ $t('mandatory') }}</span>
            </span>
            <span class="rtpl-apt-ct">{{ countImages(s) }}/{{ s.totalImages }}</span>
          </div>
        </div>
        <div class="rtpl-note">{{ $t('mandatory_sections_always_included') }}</div>
      </div>

    <!-- ③ Notas y fotografías -->
    <div class="rtpl-card rtpl-step">
      <div class="rtpl-step-h"><span class="rtpl-n">3</span><h3>{{ $t('notes_and_photographs') }}</h3></div>

      <div class="rtpl-sectabs">
        <button v-for="s in printableSections" :key="'sec_'+s.id" class="rtpl-sectab"
          :class="{ active: s.id === activeSection }" @click="activeSection = s.id">
          {{ s.name }} <span v-if="s.totalImages" class="rtpl-cnt">{{ countImages(s) }}</span>
        </button>
      </div>

      <div v-if="!printableSections.length" class="rtpl-note">{{ $t('no_results') }}</div>

      <template v-if="current">
        <!-- Notas (primero) -->
        <div class="rtpl-block-lab">{{ $t('notes') }} · {{ current.name }}</div>
        <b-input type="textarea" v-model="current.notes" size="is-small" rows="3" />

        <!-- Gantt: abrir en otra ventana + orientación (solo sección GANTT) -->
        <div v-if="current.code === 'GANTT'" class="rtpl-gantt-bar rtpl-sep-top">
          <a href="/project/activities/gantt" target="_blank" class="button is-link is-outlined is-small">
            <b-icon pack="fas" icon="external-link-alt" size="is-small"></b-icon>
            <span>{{ $t('open_gantt') }}</span>
          </a>
          <div v-if="current.gantt_user_can_change_orientation" class="rtpl-gantt-orient">
            <span class="rtpl-gantt-orient-lab">{{ $t('gantt_page_orientation') }}:</span>
            <b-select v-model="current.gantt_landscape" size="is-small">
              <option :value="false">{{ $t('portrait') }}</option>
              <option :value="true">{{ $t('landscape') }}</option>
            </b-select>
          </div>
        </div>

        <!-- Imagen principal del apartado (plano / documento de actividad), solo si la config lo define -->
        <div v-if="current.with_drawing || current.with_activity_document" class="rtpl-plano rtpl-sep-top">
          <div class="rtpl-fotohead">
            <span class="rtpl-foto-ttl">{{ $t('section_main_image') }}</span>
            <b-button size="is-small" :class="btnClass" icon-pack="fas" icon-left="drafting-compass"
              @click="selectMainResource('drawing')">{{ $t('select_drawing') }}</b-button>
            <b-button size="is-small" :class="btnClass" icon-pack="fas" icon-left="tasks"
              @click="selectMainResource('activity')">{{ $t('select_from_activity') }}</b-button>
          </div>

          <!-- Solo se muestra la imagen si está cargada; si no, estado vacío con texto. -->
          <report-image-card v-if="mainImage(current)"
            :title="current.main_title" :caption="current.main_caption"
            :title-label="$t('title')" :caption-label="$t('description')"
            :show-move="false"
            @expand="openMain" @update:title="current.main_title = $event" @update:caption="current.main_caption = $event"
            @replace="replaceMain" @remove="removeMain">
            <template #thumb>
              <img :src="mainImage(current)" class="rtpl-fill-img" />
            </template>
          </report-image-card>
          <div v-else class="rtpl-empty">
            <b-icon pack="fas" icon="image" size="is-small"></b-icon>
            <span>{{ $t('no_main_image') }}</span>
          </div>

          <!-- Leyenda del apartado: solo si la config la habilita Y hay imagen principal
               (sin imagen no hay nada que explicar con la leyenda). -->
          <template v-if="current.with_status && mainImage(current)">
            <div class="rtpl-block-lab">{{ $t('legend') }}</div>
            <div class="flex items-center gap-2 flex-wrap">
              <span v-for="(lg, i) in current.status_oc" :key="'lg_'+i" class="rtpl-legend-tag">
                <span class="rtpl-swatch" :style="{ background: lg.color }"></span>{{ lg.name }}
              </span>
              <span v-if="!current.status_oc.length" class="rtpl-note">{{ $t('no_legends') }}</span>
              <b-button type="is-primary" size="is-small" outlined icon-pack="fas" icon-left="palette" @click="legendModal = true">{{ $t('manage_legends') }}</b-button>
            </div>
          </template>
        </div>

        <!-- Fotografías (luego), solo si el apartado define imágenes extra -->
        <template v-if="current.totalImages">
          <div class="rtpl-fotohead rtpl-sep-top">
            <span class="rtpl-foto-ttl">{{ $t('photographs') }}</span>
            <span class="rtpl-foto-cnt">{{ countImages(current) }} / {{ current.totalImages }}</span>
            <b-button size="is-small" :class="btnClass" icon-pack="fas" icon-left="upload" @click="addPhotoUpload">{{ $t('upload_image') }}</b-button>
            <b-button v-if="current.code === 'GANTT'" size="is-small" :class="btnClass" icon-pack="fas" icon-left="camera" @click="openGanttCaptures">{{ $t('select_gantt_capture') }}</b-button>
            <b-button v-else size="is-small" :class="btnClass" icon-pack="fas" icon-left="tasks" @click="addPhotoFromActivity">{{ $t('select_from_activity') }}</b-button>
          </div>

          <!-- Solo se muestran las fotografías realmente cargadas; sin ninguna, estado vacío. -->
          <div v-if="loadedImages(current).length" class="rtpl-cards">
            <report-image-card v-for="(image, i) in loadedImages(current)" :key="'ph_'+image._uid"
              :meta="photoMeta(image, i)"
              :title="image.name" :caption="image.details"
              :title-label="$t('title')" :caption-label="$t('caption')"
              :can-move-up="i !== 0" :can-move-down="i !== loadedImages(current).length - 1"
              @update:title="image.name = $event" @update:caption="image.details = $event"
              @expand="openPhoto(image, i)"
              @replace="onReplacePhoto(image)"
              @remove="onRemovePhoto(image)"
              @move-up="movePhoto(image, -1)"
              @move-down="movePhoto(image, 1)">
              <template #thumb>
                <img :src="imageSrc(image)" class="rtpl-fill-img" />
              </template>
            </report-image-card>
          </div>
          <div v-else class="rtpl-empty">
            <b-icon pack="fas" icon="images" size="is-small"></b-icon>
            <span>{{ $t('no_photographs') }}</span>
          </div>
        </template>
      </template>
    </div>

    <!-- Acciones: generar informe (PDF) y solo Excel -->
    <div class="rtpl-gen-actions">
      <b-button type="is-success" icon-pack="fas" icon-left="file-export"
        :loading="isLoadingExport" @click="exportReport">{{ $t('generate_report') }}</b-button>
      <b-button type="is-primary" icon-pack="fas" icon-left="file-excel"
        :loading="isLoadingExcel" @click="exportExcel">{{ $t('generate_only_excel') }}</b-button>
    </div>

    <!-- Modal: gestión de leyendas del apartado activo -->
    <div v-if="legendModal && current" class="rtpl-modal-ov" @click.self="legendModal = false">
      <div class="rtpl-modal rtpl-modal-sm">
        <button class="rtpl-modal-x" @click="legendModal = false">✕</button>
        <h3 class="rtpl-modal-t">{{ $t('legend') }}</h3>
        <p class="rtpl-modal-cap">{{ $t('manage_legends_hint') }}</p>

        <div class="flex items-end gap-2 flex-wrap">
          <b-field :label="$t('color')" class="mb-0">
            <input type="color" v-model="newLegend.color" class="rtpl-color" />
          </b-field>
          <b-field :label="$t('text')" class="mb-0 rtpl-legend-input">
            <b-input v-model="newLegend.text" size="is-small" :placeholder="$t('legend_text')" @keyup.native.enter="addLegend" />
          </b-field>
          <b-button type="is-primary" size="is-small" icon-pack="fas" icon-left="plus" @click="addLegend">{{ $t('add') }}</b-button>
        </div>

        <div class="rtpl-tags">
          <span v-for="(lg, i) in current.status_oc" :key="'t_'+i" class="rtpl-legend-tag">
            <span class="rtpl-swatch" :style="{ background: lg.color }"></span>{{ lg.name }}
            <button class="rtpl-tag-x" :title="$t('delete')" @click="current.status_oc.splice(i, 1)">✕</button>
          </span>
          <span v-if="!current.status_oc.length" class="rtpl-note">{{ $t('no_legends') }}</span>
        </div>

        <div class="rtpl-modal-foot"><button class="rtpl-btn rtpl-btn-ghost" @click="legendModal = false">{{ $t('close') }}</button></div>
      </div>
    </div>

    <!-- Modal "Vista previa" de imagen: título de la imagen, zoom y descripción -->
    <div v-if="modal.open" class="rtpl-modal-ov" @click.self="modal.open = false">
      <div class="rtpl-modal">
        <button class="rtpl-modal-x" @click="modal.open = false">✕</button>
        <h3 class="rtpl-modal-t">{{ $t('preview') }}</h3>
        <div v-if="modal.title" class="rtpl-preview-title">{{ modal.title }}</div>

        <!-- Lienzo con padding; el zoom cambia el ancho (scroll si desborda). -->
        <div class="rtpl-preview-stage">
          <div class="rtpl-preview-inner" :style="{ width: (modal.zoom * 100) + '%' }">
            <img v-if="modal.img" :src="modal.img" class="rtpl-preview-img" />
            <div v-else class="rtpl-preview-img" v-html="modal.svg"></div>
          </div>
        </div>

        <!-- Controles de zoom -->
        <div class="rtpl-zoom">
          <b-button size="is-small" icon-pack="fas" icon-left="search-minus" :disabled="modal.zoom <= 1" @click="zoomOut" />
          <span class="rtpl-zoom-lvl">{{ Math.round(modal.zoom * 100) }}%</span>
          <b-button size="is-small" icon-pack="fas" icon-left="search-plus" :disabled="modal.zoom >= 3" @click="zoomIn" />
          <b-button size="is-small" icon-pack="fas" icon-left="compress-arrows-alt" :disabled="modal.zoom === 1" @click="zoomReset" />
        </div>

        <!-- Descripción (pie de foto) debajo -->
        <p v-if="modal.cap" class="rtpl-preview-desc">{{ modal.cap }}</p>

        <div class="rtpl-modal-foot"><button class="rtpl-btn rtpl-btn-ghost" @click="modal.open = false">{{ $t('close') }}</button></div>
      </div>
    </div>

    <!-- Subida de imagen (Título + Pie de foto): EpcModalWrapper declarativo, como manage-boqs -->
    <epc-modal-wrapper :active.sync="showUploadModal" :title="$t('upload_image')" :width="480">
      <report-image-upload-form :key="uploadKey" :show-caption="true" :is-gantt="uploadIsGantt"
        @submit="onUploadSubmit" @cancel="showUploadModal = false" />
    </epc-modal-wrapper>

    <select-image-from-activity-modal ref="refSelectResourceFromActivityModal"
      :activities="activities"
      :drawings="drawings"
      :report_sections="activityModalSections"
      @epc-select-success="handleSelectResourceFromActivity" />

    <!-- Capturas del Gantt (solo sección GANTT) -->
    <gantt-captures-modal ref="ganttCapturesModal" :project-id="project && project.id"
      @select="onGanttCaptureSelected" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { isoWeekRange, isoWeekOf, toDateInputValue, onlyNumber as onlyNumberFn, mergeImageKeepingLegends } from '../helpers/detailed-report.helper.js'
import {
  loadReportSections,
  applyLoadedReport,
  isWeeklyRangeValid,
  generateDetailedReport,
} from '../services/detailed-report.service.js'
import { fetchActivities, fetchProjectDrawings, generateExcelCurrentProgress } from '../api/index.js'
import ReportImageCard from '../components/report-image-card.vue'
import EpcModalWrapper from '·/components/epc-modal-wrapper.vue'
import ReportImageUploadForm from '../components/report-image-upload-form.vue'
import SelectImageFromActivityModal from '../components/select-image-from-activity-modal.vue'
import GanttCapturesModal from '../components/gantt-captures-modal.vue'

export default {
  name: 'report-generate-form-xelio',

  components: { ReportImageCard, EpcModalWrapper, ReportImageUploadForm, SelectImageFromActivityModal, GanttCapturesModal },

  data() {
    return {
      // Parte 1 (Periodo) — funcional: semana/año ISO + rango editable (Date, para b-datepicker).
      week: 40,
      year: 2026,
      dateFrom: null,
      dateTo: null,

      // Configuración del informe (colapsable). Autor se fija al usuario en created().
      cfgOpen: false,
      cfgTitle: 'Informe semanal de construcción',
      cfgLang: 'Español (ES)',
      cfgAuthor: '',
      // Vienen de la configuración del proyecto → se muestran deshabilitados (solo lectura).
      // (Ficha técnica = dashboard.) Gantt/Imágenes se afinarán más adelante.
      cfgInclude: [
        { key: 'ficha', labelKey: 'technical_sheet', on: true },
        { key: 'groupCharts', labelKey: 'group_charts', on: true },
        { key: 'taskTable', labelKey: 'task_table', on: true },
        { key: 'gantt', labelKey: 'Gantt', on: true },
        { key: 'images', labelKey: 'images', on: true },
      ],

      // Apartado activo (apartado 3) = id de la sección en report_sections.
      activeSection: null,
      // Apartados reales (config del proyecto, mapeado por loadReportSections).
      report_sections: [],
      // Fuentes para "Seleccionar desde actividad" (modal legacy): actividades y planos.
      activities: [],
      drawings: [],
      // Modal de subida de imagen (EpcModalWrapper declarativo, como manage-boqs). uploadKey
      // remonta el form en cada apertura para que arranque limpio.
      showUploadModal: false,
      uploadTabId: null,
      uploadImgIdx: null,
      uploadKey: 0,
      // Subida en modo Gantt (mayor resolución al comprimir).
      uploadIsGantt: false,
      // Tipo de imagen principal en curso ('drawing' | 'activity') para dejar una sola imagen.
      pendingMainType: null,
      // Estados de carga de los botones de generación.
      isLoadingExport: false,
      isLoadingExcel: false,
      // Estilo (Tailwind) de los botones "seleccionar/subir" tipo "Subir fotos / Foto actividad".
      btnClass: '!bg-white !text-blue-dark !border-gray-300 !rounded-md hover:!bg-blue-lightest',
      legendModal: false,
      newLegend: { color: '#3a6bc8', text: '' },

      modal: { open: false, title: '', cap: '', svg: '', img: '', zoom: 1 },
      _sid: 0,
    }
  },

  computed: {
    ...mapGetters({ project: 'app/project', user: 'auth/user' }),
    // Resumen "Incluye:" con los apartados marcados (traducidos).
    includeSummary() {
      return this.cfgInclude.filter((i) => i.on).map((i) => this.$t(i.labelKey)).join(', ')
    },
    // Apartados visibles (como en el panel legacy: v-if tab.visible).
    visibleSections() {
      return (this.report_sections || []).filter((s) => s.visible)
    },
    // Apartados incluidos en el informe (apartado 3). Igual que el legacy (:visible="tab.print"):
    // el apartado 3 coincide siempre con el 2 y oculta los que se desmarcan en él.
    printableSections() {
      return (this.report_sections || []).filter((s) => s.print)
    },
    // Apartado activo del apartado 3 (por id).
    current() {
      return (this.report_sections || []).find((s) => s.id === this.activeSection) || null
    },
    // Índice del apartado activo dentro de report_sections (lo necesita el modal de actividad).
    currentIndex() {
      return (this.report_sections || []).findIndex((s) => s.id === this.activeSection)
    },
    // Clon de los apartados para el modal de actividad (que reemplaza el array al confirmar).
    // IMPORTANTE: NO usar JSON.stringify — destruiría los File de las imágenes subidas. Hacemos
    // copia superficial por nivel manteniendo `image` (File/Blob/URL) por referencia.
    activityModalSections() {
      return (this.report_sections || []).map((s) => ({
        ...s,
        images: (s.images || []).map((im) => ({ ...im })),
        status_oc: Array.isArray(s.status_oc) ? s.status_oc.map((l) => ({ ...l })) : [],
      }))
    },
    // ¿Las fechas (Date) coinciden exactamente con la semana ISO seleccionada?
    isoComplete() {
      if (!this.dateFrom || !this.dateTo) return false
      const r = isoWeekRange(this.week, this.year)
      return !!r && toDateInputValue(this.dateFrom) === r.from && toDateInputValue(this.dateTo) === r.to
    },
    // Etiqueta del rango para el chip (d mmm yyyy – d mmm yyyy).
    periodLabel() {
      if (!this.dateFrom || !this.dateTo) return '—'
      return this.fmt(this.dateFrom) + ' – ' + this.fmt(this.dateTo)
    },
  },

  created() {
    // Secuencia para el id estable (_uid) de las fotos (no reactiva: solo alimenta las :key).
    this._uidSeq = 0
    // Semana/año ISO ACTUAL por defecto, y el rango lunes–domingo correspondiente.
    const now = isoWeekOf(new Date())
    this.week = now.week
    this.year = now.year
    this.adjustToIso()
    // Autor por defecto = nombre del usuario actual (read-only por ahora).
    this.cfgAuthor = (this.user && (this.user.name || this.user.fullname)) || ''
    // "Contenido a incluir" según el config real del proyecto.
    this.deriveInclude()
    // Apartados reales.
    this.loadSections()
    // Fuentes para "Seleccionar desde actividad".
    this.getActivities()
    this.getDrawings()
  },

  methods: {
    // Restringe a dígitos (además de type=number). Delega en el helper puro.
    onlyNumber(event) { return onlyNumberFn(event) },

    // Carga de actividades/planos del proyecto para el modal de selección desde actividad.
    async getActivities() {
      if (!this.project) return
      try { const data = await fetchActivities(this.project.id); this.activities = data.activities || [] } catch (e) { /* noop */ }
    },
    async getDrawings() {
      if (!this.project) return
      try { const data = await fetchProjectDrawings(this.project.id); if (data && data.success) this.drawings = data.documents || [] } catch (e) { /* noop */ }
    },

    // Primer slot de imagen libre del apartado activo (-1 si están todos ocupados).
    firstEmptyImageIndex() {
      if (!this.current) return -1
      return (this.current.images || []).findIndex((im) => !im.image)
    },

    // Abre el modal de subida apuntando a un slot concreto del apartado activo.
    openUploadForSlot(slotIdx) {
      this.uploadTabId = this.current.id
      this.uploadImgIdx = slotIdx
      this.uploadIsGantt = this.current.code === 'GANTT'
      this.uploadKey++
      this.showUploadModal = true
    },

    // "Seleccionar captura del Gantt": abre el modal de capturas sobre el primer slot libre.
    openGanttCaptures() {
      const idx = this.firstEmptyImageIndex()
      if (idx < 0) { this.$buefy.toast.open({ message: this.$t('image_limit_reached'), type: 'is-warning' }); return }
      this.uploadTabId = this.current.id
      this.uploadImgIdx = idx
      this.$refs.ganttCapturesModal.show()
    },

    // Captura del Gantt elegida (emit del modal): la coloca en su slot conservando leyendas.
    onGanttCaptureSelected(image) {
      const section = this.report_sections.find((tab) => tab.id == this.uploadTabId)
      if (section) {
        section.images.splice(this.uploadImgIdx, 1, mergeImageKeepingLegends(section.images[this.uploadImgIdx], image))
      }
    },

    // Abre el modal de selección desde actividad apuntando a un slot concreto.
    openActivityForSlot(slotIdx) {
      this.$refs.refSelectResourceFromActivityModal.show({
        tabIndex: this.currentIndex, imageIndex: slotIdx, url: '',
        activity_id: null, subactivity_id: null, document_id: null,
        subactivities: [], documents: [], type: 'image',
      })
    },

    // "Subir imagen": abre el modal sobre el primer slot libre.
    addPhotoUpload() {
      const idx = this.firstEmptyImageIndex()
      if (idx < 0) { this.$buefy.toast.open({ message: this.$t('image_limit_reached'), type: 'is-warning' }); return }
      this.openUploadForSlot(idx)
    },

    // "Seleccionar desde actividad": abre el modal sobre el primer slot libre.
    addPhotoFromActivity() {
      const idx = this.firstEmptyImageIndex()
      if (idx < 0) { this.$buefy.toast.open({ message: this.$t('image_limit_reached'), type: 'is-warning' }); return }
      this.openActivityForSlot(idx)
    },

    // "Cambiar": reabre el modal correcto sobre el MISMO slot según el origen de la foto.
    onReplacePhoto(image) {
      const slotIdx = this.current ? this.current.images.indexOf(image) : -1
      if (slotIdx < 0) return
      if (image.is_activity_document) this.openActivityForSlot(slotIdx)
      else this.openUploadForSlot(slotIdx)
    },

    // Subir/bajar: intercambia esta foto con la foto cargada adyacente (dir -1 = arriba, 1 = abajo).
    movePhoto(image, dir) {
      if (!this.current) return
      const loaded = this.loadedImages(this.current)
      const li = loaded.indexOf(image)
      const target = li + dir
      if (li < 0 || target < 0 || target >= loaded.length) return
      const a = this.current.images.indexOf(loaded[li])
      const b = this.current.images.indexOf(loaded[target])
      const tmp = this.current.images[a]
      this.$set(this.current.images, a, this.current.images[b])
      this.$set(this.current.images, b, tmp)
    },

    // "Eliminar": limpia el slot (imagen, origen y leyendas) dejándolo vacío.
    onRemovePhoto(image) {
      const slotIdx = this.current ? this.current.images.indexOf(image) : -1
      if (slotIdx < 0) return
      this.current.images.splice(slotIdx, 1, {
        id: slotIdx + 1,
        name: this.$t('image_number', { number: slotIdx + 1 }),
        image: null, details: '', custom_legends: false, legends: [],
      })
    },

    // Imagen subida (emit del form): la coloca en su slot conservando leyendas y cierra.
    onUploadSubmit({ name, details, image }) {
      const section = this.report_sections.find((tab) => tab.id == this.uploadTabId)
      if (section) {
        section.images.splice(this.uploadImgIdx, 1, mergeImageKeepingLegends(section.images[this.uploadImgIdx], { name, details, image }))
      }
      this.showUploadModal = false
    },

    // Selección desde actividad (emit del modal): devuelve el array de apartados actualizado.
    // Si era para la imagen principal, dejamos UNA sola imagen (limpiamos la otra fuente).
    handleSelectResourceFromActivity(sections) {
      this.report_sections = sections
      if (this.pendingMainType) {
        const sec = (this.report_sections || []).find((s) => s.id === this.activeSection)
        if (sec) {
          if (this.pendingMainType === 'drawing') sec.activity_image = ''
          else sec.drawing_image = ''
        }
        this.pendingMainType = null
      }
    },

    // Carga los apartados reales del config, igual que el legacy (project.config crudo si
    // existe; si no, el backend mapeado). Cada sección trae visible/disabled/print/name/images.
    async loadSections() {
      if (!this.project) return
      try {
        const sections = await loadReportSections(this.project)
        // Notas por apartado (campo local) + id estable: algunas secciones del config no traen
        // `id`, lo que duplicaba claves (sec_undefined) y rompía el tab activo. Sintetizamos uno.
        sections.forEach((s, i) => {
          if (s.notes === undefined) s.notes = ''
          // Título/pie de la imagen principal, por apartado (para que viajen en el payload).
          if (s.main_title === undefined) s.main_title = ''
          if (s.main_caption === undefined) s.main_caption = ''
          if (s.id === undefined || s.id === null) s.id = s.code ? ('sec_' + s.code + '_' + i) : ('sec_' + i)
        })
        this.report_sections = sections
        // Apartado 3 arranca en el primer apartado incluido (print), o el primero si ninguno.
        const first = sections.find((s) => s.print) || sections[0]
        this.activeSection = first ? first.id : null
      } catch (e) { /* sin config */ }
    },

    // Genera el informe (PDF): valida el rango, arma los infoTabs (sube imágenes por Vapor) y
    // llama al backend. Reusa los mismos servicios que el legacy; mapea dateFrom/dateTo→weeklyRange.
    async exportReport() {
      if (this.isLoadingExport) return
      const range = [this.dateFrom, this.dateTo]
      if (!isWeeklyRangeValid(range)) {
        this.$buefy.toast.open({ message: this.$t('select_week_range_required'), type: 'is-warning', position: 'is-top-right' })
        return
      }
      this.isLoadingExport = true
      this.$notify.success('generating_report')
      try {
        await generateDetailedReport(this.project.id, {
          report_sections: this.report_sections,
          weeklyRangeForm: range,
          week: this.week,
          s3bucket: window.config.s3bucket,
        })
        this.$notify.success('generating_report')
        this.$emit('refresh-history')
      } catch (error) {
        this.$notify.error((error && error.error_msg) || 'error_retrieving_data')
      } finally {
        this.isLoadingExport = false
      }
    },

    // Genera solo el Excel de avance actual del proyecto (igual que el legacy).
    async exportExcel() {
      if (this.isLoadingExcel) return
      this.isLoadingExcel = true
      this.$notify.success('generating_report')
      try {
        await generateExcelCurrentProgress(this.project.id)
        this.$notify.success('generating_report')
      } catch (error) {
        this.$notify.error((error && error.error_msg) || 'error_retrieving_data')
      } finally {
        this.isLoadingExcel = false
      }
    },

    // Carga un informe guardado (histórico → "volver a generar"): vuelca imágenes/leyendas/
    // estados en report_sections (servicio applyLoadedReport, igual que el legacy) y mapea la
    // semana/rango al modelo del x-elio (week/year + dateFrom/dateTo).
    async LoadReport(report) {
      if (!this.report_sections || !this.report_sections.length) await this.loadSections()
      const { week, weeklyRangeForm } = applyLoadedReport(this.report_sections, report)
      if (week != null) this.week = week
      if (Array.isArray(weeklyRangeForm) && weeklyRangeForm.length >= 2) {
        this.dateFrom = weeklyRangeForm[0]
        this.dateTo = weeklyRangeForm[1]
        this.year = isoWeekOf(weeklyRangeForm[0]).year
      }
    },

    // Si el apartado activo deja de estar incluido (print=false), salta al primero incluido.
    ensureActivePrintable() {
      const list = this.printableSections
      if (!list.length) { this.activeSection = null; return }
      if (!list.some((s) => s.id === this.activeSection)) this.activeSection = list[0].id
    },

    // Alterna la inclusión de un apartado (las obligatorias no se pueden desactivar).
    // Al cambiar la inclusión, el apartado 3 se recoloca en un apartado incluido.
    toggleSection(section) {
      if (section.disabled) return
      section.print = !section.print
      this.ensureActivePrintable()
    },

    // Fotografías realmente cargadas de un apartado (slots con imagen). A cada una le damos un
    // id estable `_uid` (si no lo tiene) para usarlo como :key: así al reordenar Vue MUEVE el
    // nodo en vez de recrear su contenido (evita el "flash" de recarga del <img>).
    loadedImages(section) {
      const imgs = (section && section.images ? section.images : []).filter((im) => im.image)
      imgs.forEach((im) => { if (im._uid == null) im._uid = ++this._uidSeq })
      return imgs
    },

    // src mostrable de una imagen: URL directa (string / de actividad) o objectURL de un File
    // subido (igual que el getImagePreview del legacy). Cachea el objectURL en el propio slot.
    imageSrc(image) {
      const img = typeof image === 'string' ? { image } : image
      if (!img || !img.image) return ''
      if (img.is_activity_document || typeof img.image === 'string') return img.image
      if (img.image instanceof File || img.image instanceof Blob) {
        if (!img._previewUrl) img._previewUrl = URL.createObjectURL(img.image)
        return img._previewUrl
      }
      return ''
    },

    // Línea de origen de la fotografía: "Foto N de TOTAL · Personalizada | De: <actividad>".
    photoMeta(image, index) {
      const total = this.current ? this.current.totalImages : 0
      let source
      if (image.is_activity_document) {
        source = image.source_name ? this.$t('from_activity_x', { name: image.source_name }) : this.$t('from_activity')
      } else {
        source = this.$t('custom_image')
      }
      return this.$t('photo_number', { number: index + 1, total }) + ' · ' + source
    },

    // Imagen principal del apartado: la que esté puesta (plano o documento de actividad).
    mainImage(section) {
      if (!section) return ''
      return section.drawing_image || section.activity_image || ''
    },

    // Selecciona la imagen principal (una sola) desde el modal de actividad, como el clásico:
    // type 'drawing' → plano (drawing_image); type 'activity' → documento de actividad.
    selectMainResource(type) {
      this.pendingMainType = type
      this.$refs.refSelectResourceFromActivityModal.show({
        tabIndex: this.currentIndex, imageIndex: 0, type,
        url: '', activity_id: null, subactivity_id: null, document_id: null,
        subactivities: [], documents: [],
      })
    },

    // "Cambiar" la imagen principal: reabre el modal con la fuente actual (plano o actividad).
    replaceMain() {
      if (!this.current) return
      this.selectMainResource(this.current.drawing_image ? 'drawing' : 'activity')
    },

    // "Eliminar" la imagen principal: limpia ambas fuentes.
    removeMain() {
      if (!this.current) return
      this.current.drawing_image = ''
      this.current.activity_image = ''
    },

    // Nº de imágenes ya cargadas en un apartado (sobre su total de imágenes extra).
    countImages(section) {
      return (section.images || []).filter((im) => im.image).length
    },

    // "Contenido a incluir" derivado del config del proyecto (reports.detailed). Read-only:
    // dashboard=RAG_SUMMARY, gráficos por grupos=LINE_SELECTOR, tabla=TASK_COLUMNS_SELECTOR
    // (pages), gantt=GANTT, imágenes=GENERAL (sections). Si no hay config, deja los defaults.
    deriveInclude() {
      const ag = this.project && this.project.config && this.project.config.activity_groups
      const d = ag && ag.reports && ag.reports.detailed
      if (!d) return
      const pages = new Set((d.pages || []).filter((p) => p.visible !== false).map((p) => p.code))
      const sections = (d.sections || []).filter((s) => s.visible !== false).map((s) => String(s.code || ''))
      const on = {
        ficha: pages.has('RAG_SUMMARY'),
        groupCharts: pages.has('LINE_SELECTOR'),
        taskTable: pages.has('TASK_COLUMNS_SELECTOR'),
        gantt: sections.indexOf('GANTT') >= 0,
        images: sections.some((c) => c.indexOf('GENERAL') === 0),
      }
      this.cfgInclude.forEach((it) => { if (it.key in on) it.on = on[it.key] })
    },

    // Fija Desde/Hasta (Date) al lunes–domingo de la semana ISO seleccionada (reglas ISO 8601).
    adjustToIso() {
      const r = isoWeekRange(this.week, this.year)
      if (r) { this.dateFrom = this.parseISO(r.from); this.dateTo = this.parseISO(r.to) }
    },

    // 'yyyy-mm-dd' → Date local (sin desfase de zona horaria).
    parseISO(str) {
      const p = str.split('-').map(Number)
      return new Date(p[0], p[1] - 1, p[2])
    },

    // Date → 'd mmm yyyy' (mes corto en español) para mostrar.
    fmt(date) {
      if (!date) return ''
      const m = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sept', 'oct', 'nov', 'dic']
      return date.getDate() + ' ' + m[date.getMonth()] + ' ' + date.getFullYear()
    },

    // Ilustración sintética de planta solar (thumbnail / modal grande con `big`).
    solar(withHouse, big) {
      const gid = 'rtsky' + (this._sid++)
      const house = withHouse
        ? '<polygon points="206,92 214,83 224,92" fill="#c7c9ba"/><rect x="207" y="92" width="16" height="17" fill="#eef0ea" stroke="#9aa08a"/>'
        : ''
      let panels = ''
      for (let r = 0; r < 6; r++) {
        const y = 106 + r * 14, h = 7 + r * 1.7, w = 20 + r * 2.4, gap = 30 + r * 4.5, skew = 6 + r
        const off = -12 - r * 5 + (r % 2 ? gap / 2 : 0)
        for (let c = -1; c < 11; c++) {
          const x = off + c * gap
          if (x < -w - skew || x > 332) continue
          panels += '<path d="M' + x + ' ' + y + ' L' + (x + w) + ' ' + y + ' L' + (x + w - skew) + ' ' + (y + h) + ' L' + (x - skew) + ' ' + (y + h) + ' Z" fill="#2c3d5d" opacity="' + (0.95 - r * 0.04).toFixed(2) + '"/>'
        }
      }
      const label = big
        ? '<rect x="14" y="170" width="214" height="20" rx="3" fill="#161f2b" opacity=".62"/><text x="22" y="184" fill="#fff" font-size="9" letter-spacing="1.2" font-weight="700" font-family="system-ui">ILUSTRACIÓN SOLAR · ENTORNO DE DEMOSTRACIÓN</text>'
        : ''
      return '<svg viewBox="0 0 320 200" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg">'
        + '<defs><linearGradient id="' + gid + '" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#cfdae2"/><stop offset="1" stop-color="#bbccd7"/></linearGradient></defs>'
        + '<rect width="320" height="200" fill="url(#' + gid + ')"/>'
        + '<circle cx="256" cy="46" r="19" fill="#ece7cf"/>'
        + '<polygon points="0,116 72,68 140,108 212,64 282,104 320,84 320,132 0,132" fill="#aeb295" opacity=".85"/>'
        + '<polygon points="0,126 60,96 132,122 202,92 272,120 320,100 320,142 0,142" fill="#9aa07f"/>'
        + '<rect y="120" width="320" height="80" fill="#d9d0a9"/>' + house + panels + label + '</svg>'
    },

    // Amplía una fotografía del apartado (imagen real si existe; si no, ilustración demo).
    openPhoto(image, i) {
      const src = this.imageSrc(image)
      this.modal = {
        open: true,
        title: (image && image.name) || this.$t('image_number', { number: i + 1 }),
        cap: (image && image.details) || '',
        svg: src ? '' : this.solar(i % 2 === 1, true),
        img: src,
        zoom: 1,
      }
    },

    // Amplía la imagen principal del apartado activo.
    openMain() {
      const src = this.mainImage(this.current)
      this.modal = {
        open: true,
        title: (this.current && this.current.main_title) || this.$t('section_main_image'),
        cap: (this.current && this.current.main_caption) || '',
        svg: src ? '' : this.solar(false, true),
        img: src || '',
        zoom: 1,
      }
    },

    // Zoom del modal de vista previa (ancho 100%–300% en pasos de 50%).
    zoomIn() { this.modal.zoom = Math.min(3, Math.round((this.modal.zoom + 0.5) * 10) / 10) },
    zoomOut() { this.modal.zoom = Math.max(1, Math.round((this.modal.zoom - 0.5) * 10) / 10) },
    zoomReset() { this.modal.zoom = 1 },

    // Añade una leyenda al apartado activo (status_oc = { name, color }, como el legacy).
    addLegend() {
      if (!this.current || !this.newLegend.text.trim()) return
      this.current.status_oc.push({ name: this.newLegend.text.trim(), color: this.newLegend.color })
      this.newLegend.text = ''
    },
  },
}
</script>

<style scoped>
.rtpl { display: flex; flex-direction: column; gap: 16px; color: #16203a; font-size: 14px; }
.rtpl h2, .rtpl h3 { margin: 0; font-weight: 700; }

/* Labels de los b-field (Buefy): solo reducimos el tamaño (color por defecto). */
.rtpl ::v-deep .field > .label { font-size: 0.72rem; margin-bottom: 0.2rem; }

.rtpl-card { background: #fff; border: 1px solid #e2e7ef; border-radius: 10px; }
.rtpl-step { padding: 18px 20px; }
.rtpl-step-h { display: flex; align-items: center; gap: 11px; margin-bottom: 14px; flex-wrap: wrap; }
.rtpl-step-h h3 { font-size: 17px; color: #1b3d94; }
.rtpl-n { width: 26px; height: 26px; border-radius: 50%; background: #2456d6; color: #fff; display: grid; place-items: center; font-size: 13px; font-weight: 800; flex: none; }
.rtpl-chip { font-size: 12.5px; font-weight: 700; color: #1b3d94; background: #e8eefc; border-radius: 999px; padding: 3px 11px; }
.rtpl-collapse { border: 1px solid rgba(36,86,214,.45); border-radius: 6px; padding: 14px 16px; margin-top: 16px; }
.rtpl-collapse-h { display: flex; align-items: center; gap: 8px; color: #1b3d94; cursor: pointer; }
.rtpl-collapse-t { font-weight: 700; color: #1b3d94; font-size: 13.5px; }
.rtpl-cfg-edit { color: #1b3d94; font-size: 13px; }
.rtpl-collapse-h:hover .rtpl-cfg-edit { text-decoration: underline; }
.rtpl-cfg-summary { font-size: 12.5px; color: #697289; line-height: 1.65; margin-top: 10px; }

.rtpl-btn { font: inherit; font-size: 13px; font-weight: 600; border-radius: 6px; padding: 8px 13px; cursor: pointer; border: 1px solid transparent; }
.rtpl-btn-sm { padding: 6px 10px; font-size: 12.5px; }
.rtpl-btn-primary { background: #2456d6; color: #fff; }
.rtpl-btn-out { background: #fff; color: #1b3d94; border-color: #cdd5e2; }
.rtpl-btn-ghost { background: #fff; color: #16203a; border-color: #e2e7ef; }

.rtpl-block-lab { font-weight: 700; color: #1b3d94; font-size: 13.5px; margin: 16px 0 7px; }

/* Acciones de generación (al final): centradas. */
.rtpl-gen-actions { display: flex; justify-content: center; gap: 10px; flex-wrap: wrap; margin-top: 4px; }

/* Barra Gantt: abrir en otra ventana + selector de orientación (una sola línea). */
.rtpl-gantt-bar { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.rtpl-gantt-orient { display: flex; align-items: center; gap: 8px; }
.rtpl-gantt-orient-lab { font-size: 12.5px; color: #1b3d94; font-weight: 600; white-space: nowrap; }

.rtpl-apt { display: flex; align-items: center; gap: 8px; background: #f2f6fd; border: 1px solid #dbe4f3; border-radius: 6px; padding: 6px 10px; font-size: 12.5px; }
/* El texto (nombre + · Obligatoria) ocupa el espacio y se recorta con … sin deformar la tarjeta. */
.rtpl-apt-label { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rtpl-apt-name { font-weight: 600; }
.rtpl-apt-ob { font-size: 11px; color: #697289; }
.rtpl-apt-ct { flex: none; font-size: 11.5px; color: #697289; }
.rtpl-toggle { width: 32px; height: 18px; border-radius: 999px; background: #cdd5e2; position: relative; cursor: pointer; flex: none; transition: background .15s; }
.rtpl-toggle::after { content: ""; position: absolute; top: 2px; left: 2px; width: 14px; height: 14px; border-radius: 50%; background: #fff; transition: left .15s; }
.rtpl-toggle.on { background: #2456d6; }
.rtpl-toggle.on::after { left: 16px; }
.rtpl-toggle.is-locked { cursor: default; opacity: .7; }
.rtpl-note { font-size: 12px; color: #697289; margin-top: 10px; font-weight: 400; }

.rtpl-sectabs { display: flex; gap: 2px; border-bottom: 1px solid #e2e7ef; flex-wrap: wrap; margin-bottom: 16px; }
.rtpl-sectab { font: inherit; font-size: 13.5px; font-weight: 600; color: #697289; padding: 9px 12px; cursor: pointer; background: none; border: none; position: relative; display: inline-flex; align-items: center; gap: 7px; }
.rtpl-sectab.active { color: #1b3d94; font-weight: 700; }
.rtpl-sectab.active::after { content: ""; position: absolute; left: 10px; right: 10px; bottom: -1px; height: 2.5px; background: #2456d6; border-radius: 3px; }
.rtpl-cnt { font-size: 11px; background: #f2f6fd; border: 1px solid #e2e7ef; color: #697289; border-radius: 999px; padding: 0 7px; font-weight: 700; }

/* Separador superior (Notas, bajo las fotografías). */
.rtpl-sep-top { border-top: 1px solid #e2e7ef; margin-top: 18px; padding-top: 16px; }

.rtpl-fotohead { display: flex; align-items: center; gap: 12px; margin: 18px 0 10px; flex-wrap: wrap; }
.rtpl-foto-ttl { font-weight: 800; color: #1b3d94; font-size: 16px; }
.rtpl-foto-cnt { color: #697289; font-size: 12.5px; }

/* Lista de tarjetas de foto (report-image-card). */
.rtpl-cards { display: flex; flex-direction: column; gap: 12px; }
.rtpl-fill { width: 100%; height: 100%; }

.rtpl-plano { margin-top: 4px; }
/* Leyenda del plano: selector de color + texto + lista. */
.rtpl-color { width: 34px; height: 30px; padding: 0; border: 1px solid #cdd5e2; border-radius: 6px; background: #fff; cursor: pointer; flex: none; }
.rtpl-legend-input { flex: 1; max-width: 320px; }
.rtpl-swatch { width: 14px; height: 14px; border-radius: 3px; border: 1px solid rgba(0,0,0,.12); flex: none; }
/* Leyenda como etiquetas (tags) */
.rtpl-legend-tag { display: inline-flex; align-items: center; gap: 6px; background: #f2f6fd; border: 1px solid #dbe4f3; border-radius: 999px; padding: 3px 10px; font-size: 12.5px; color: #16203a; }
.rtpl-tag-x { background: none; border: none; cursor: pointer; color: #9aa3b5; font-size: 11px; line-height: 1; padding: 0 0 0 2px; }
.rtpl-tag-x:hover { color: #cc1f1a; }
.rtpl-tags { display: flex; flex-wrap: wrap; gap: 8px; margin: 16px 0; }
.rtpl-modal-sm { max-width: 520px; }

/* z-index por encima del sidebar (60) y navbar (50) para que el overlay cubra y bloquee TODO
   (si no, el menú lateral queda encima y se puede clicar con el modal abierto). */
.rtpl-modal-ov { position: fixed; inset: 0; background: rgba(15,23,42,.55); display: flex; align-items: center; justify-content: center; padding: 24px; z-index: 1000; }

.rtpl-modal { background: #fff; border-radius: 12px; width: 100%; max-width: 760px; max-height: 92vh; overflow: auto; padding: 22px 24px 20px; position: relative; box-shadow: 0 20px 60px rgba(15,23,42,.35); }
.rtpl-modal-x { position: absolute; top: 15px; right: 16px; border: none; background: none; cursor: pointer; font-size: 16px; color: #697289; }
.rtpl-modal-t { color: #1b3d94; font-size: 18px; }
.rtpl-modal-cap { color: #697289; font-size: 13.5px; margin: 7px 0 14px; }
/* Modal "Vista previa": título de la imagen, lienzo con padding + zoom y descripción. */
.rtpl-preview-title { font-weight: 700; color: #16203a; font-size: 14px; margin: 2px 0 10px; }
.rtpl-preview-stage { background: #f7f9fc; border: 1px solid #e2e7ef; border-radius: 8px; padding: 16px; overflow: auto; max-height: 64vh; }
.rtpl-preview-inner { margin: 0 auto; transition: width .15s ease; }
.rtpl-preview-img { display: block; width: 100%; height: auto; border-radius: 4px; }
.rtpl-preview-inner >>> svg { display: block; width: 100%; height: auto; }
.rtpl-zoom { display: flex; align-items: center; justify-content: center; gap: 8px; margin-top: 12px; }
.rtpl-zoom-lvl { font-size: 12.5px; font-weight: 700; color: #697289; min-width: 44px; text-align: center; }
.rtpl-preview-desc { color: #697289; font-size: 13px; line-height: 1.6; margin: 12px 0 0; text-align: center; }
.rtpl-fill-img { width: 100%; height: 100%; object-fit: cover; display: block; }
.rtpl-empty { display: flex; align-items: center; gap: 8px; justify-content: center; color: #8a93a6; font-size: 13px; background: #f7f9fc; border: 1px dashed #d7deea; border-radius: 8px; padding: 18px; margin-top: 8px; }
.rtpl-modal-foot { display: flex; justify-content: flex-end; margin-top: 16px; }

@media (max-width: 960px) {
  .rtpl-legend-input { max-width: none; }
}
</style>
